import { createUndoableCommit, UNDO_WINDOW_MS, type PendingCommit, type Timers } from './undoableCommit';

/**
 * P-27 (D-166 s.11) - "בטל" for a delete and for a status change. Pure and
 * framework-free (unit-tested without a DOM); `undoableActions.ts` wires it to
 * the one toast component, the pending-delete store and the API.
 *
 *  - Hard delete: the row is hidden at once and the DELETE request leaves only
 *    after `windowMs` (6 s). "בטל" cancels the request (nothing was sent). Closing
 *    the tab / leaving the page sends it at once (`flushAll`), so it is never lost.
 *  - Status change: applied at once; "בטל" sends the previous status through the
 *    same route.
 */
export interface DeleteJob {
  kind: string;
  id: string;
  /** Toast text, already translated ("המשימה נמחקה"). */
  message: string;
  /** The DELETE request. */
  run: () => Promise<unknown>;
  /**
   * The same DELETE as a plain HTTP request, for when the tab is closing: an
   * XHR is cancelled by the browser then, a `fetch` with `keepalive` is not.
   */
  unload?: UnloadRequest;
  onCommitted?: () => void;
  /** Undone or failed: the row is back. */
  onRestore?: () => void;
  onError?: (error: unknown) => void;
}

export interface UnloadRequest {
  method: 'DELETE' | 'POST' | 'PATCH';
  /** Path under the API base URL, e.g. `/tickets/<id>`. */
  path: string;
  body?: unknown;
}

/**
 * What a keepalive sender answers: `false` = not handed to the browser (the
 * job is flushed the normal way); `true` = handed over, outcome unknown; a
 * promise = handed over, settles with the request (rejects with the API's
 * error body on a refusal or a network failure).
 */
export type KeepaliveResult = boolean | Promise<unknown>;

/**
 * Builds the sender used on pagehide / visibilitychange=hidden: `fetch` with
 * `keepalive: true` and the same Authorization header as the api client.
 * FIX-B9 (QA-P27 minor): the returned promise rejects when the request failed,
 * so a page that is still alive can bring the row back.
 */
export function makeKeepaliveSender(
  fetchFn: (url: string, init: Record<string, unknown>) => Promise<unknown>,
  baseUrl: string,
  getToken: () => string | null,
) {
  return (req: UnloadRequest): KeepaliveResult => {
    try {
      const token = getToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const sent = fetchFn(`${baseUrl}${req.path}`, {
        method: req.method,
        keepalive: true,
        headers,
        ...(req.body !== undefined ? { body: JSON.stringify(req.body) } : {}),
      });
      const outcome = Promise.resolve(sent).then(async (res) => {
        const r = res as { ok?: boolean; status?: number; json?: () => Promise<unknown> } | undefined;
        if (!r || typeof r.ok !== 'boolean' || r.ok) return res;
        const body = r.json ? await r.json().catch(() => null) : null;
        // The api client's rejection shape: the API body + `response.{status,data}`.
        throw body && typeof body === 'object'
          ? { ...(body as object), response: { status: r.status, data: body } }
          : { error: { code: r.status }, response: { status: r.status } };
      });
      outcome.catch(() => undefined); // never an unhandled rejection when nobody listens
      return outcome;
    } catch {
      return false;
    }
  };
}

export interface SchedulerDeps {
  windowMs?: number;
  timers?: Timers;
  /** Show the toast with an undo action; returns the toast id. */
  show: (message: string, onUndo: () => void) => string;
  dismiss: (toastId: string) => void;
  setHidden: (kind: string, id: string, hidden: boolean) => void;
  /** Called after a successful undo (announce "הפעולה בוטלה"). */
  undone?: () => void;
  /** Called when the delayed request failed (error toast). */
  failed?: (error: unknown, job: DeleteJob) => void;
  /**
   * FIX-B9 (QA-P27 minor): run `fn` once the page is visible again (now, when it
   * is). A keepalive DELETE sent while the tab was hidden that failed brings its
   * row back and shows the error only when the user can see it.
   */
  whenVisible?: (fn: () => void) => void;
}

export function createDeleteScheduler(deps: SchedulerDeps) {
  const toastOf = new Map<number, string>();
  const active = new Map<number, { job: DeleteJob; pending: PendingCommit }>();
  const committer = createUndoableCommit<DeleteJob, unknown>({
    windowMs: deps.windowMs ?? UNDO_WINDOW_MS,
    timers: deps.timers,
    commit: (job) => job.run(),
    onCommitted: (_r, job, id) => {
      active.delete(id);
      dismissToast(id);
      job.onCommitted?.(); // the row stays hidden: it no longer exists on the server
    },
    onUndone: (job, id) => {
      active.delete(id);
      dismissToast(id);
      deps.setHidden(job.kind, job.id, false);
      job.onRestore?.();
      deps.undone?.();
    },
    onError: (error, job, id) => {
      active.delete(id);
      dismissToast(id);
      deps.setHidden(job.kind, job.id, false); // it was not deleted: show it again
      job.onRestore?.();
      if (job.onError) job.onError(error);
      else deps.failed?.(error, job);
    },
  });

  function dismissToast(id: number) {
    const toastId = toastOf.get(id);
    toastOf.delete(id);
    if (toastId) deps.dismiss(toastId);
  }

  return {
    schedule(job: DeleteJob): PendingCommit {
      deps.setHidden(job.kind, job.id, true);
      const pending = committer.schedule(job);
      active.set(pending.id, { job, pending });
      toastOf.set(pending.id, deps.show(job.message, () => { pending.undo(); }));
      return pending;
    },
    /**
     * The tab is closing / hidden: hand every waiting DELETE to the browser as a
     * keepalive request (it survives the page), then settle the job without a
     * second request. A job with no `unload` is flushed the normal way.
     */
    async flushOnHide(send: (req: UnloadRequest) => KeepaliveResult): Promise<void> {
      const waiting = [...active.values()].filter((a) => a.pending.state() === 'pending');
      await Promise.all(waiting.map(({ job, pending }) => {
        const sent = job.unload ? send(job.unload) : false;
        if (sent !== false) {
          // Already sent: no second request. If the page lives on and the request
          // failed, the job fails (row back + error), once the page is visible.
          const outcome = sent === true ? Promise.resolve() : sent;
          job.run = () => outcome.catch((err) => new Promise((_resolve, reject) => {
            if (deps.whenVisible) deps.whenVisible(() => reject(err));
            else reject(err);
          }));
        }
        return pending.flush();
      }));
    },
    flushAll: () => committer.flushAll(),
    pendingCount: () => committer.pendingCount(),
  };
}

export interface StatusUndoOptions {
  /** Toast text, already translated ("הסטטוס שונה ל…"). */
  message: string;
  /** Send the previous status through the same route. */
  revert: () => Promise<unknown>;
  onReverted?: () => void;
  onRevertFailed?: (error: unknown) => void;
}

/**
 * Offer "בטל" after a status change that already happened. The toast lives for
 * the undo window; the revert is a normal request (and gets the normal audit).
 */
/** A revert resolves to this when it needs the person's input (e.g. the D-303 warranty window opened): the
 *  undo is NOT done yet, so no "reverted" callback and no "undone" announcement. */
export const UNDO_DEFERRED = Symbol('undo-deferred');

export function offerStatusUndo(
  deps: { show: (message: string, onUndo: () => void) => string; dismiss: (toastId: string) => void; undone?: () => void },
  opts: StatusUndoOptions
): void {
  let used = false;
  const toastId = deps.show(opts.message, () => {
    if (used) return;
    used = true;
    void opts.revert().then(
      (r) => { if (r === UNDO_DEFERRED) return; opts.onReverted?.(); deps.undone?.(); },
      (err) => { opts.onRevertFailed?.(err); },
    );
  });
  void toastId;
}
